import { Plus, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ProviderFormState, ProviderContact } from "./types";

const inputClass =
  "w-full h-10 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors";
const labelClass = "text-micro block mb-2";

interface Props {
  form: ProviderFormState;
  update: (field: string, value: any) => void;
}

function generateId() {
  return crypto.randomUUID();
}

export default function TabKontakte({ form, update }: Props) {
  const addContact = () => {
    const newContact: ProviderContact = {
      id: generateId(),
      name: "",
      role: "",
      email: "",
      phone: "",
      is_primary: form.contacts.length === 0,
    };
    update("contacts", [...form.contacts, newContact]);
  };

  const removeContact = (id: string) => {
    const remaining = form.contacts.filter((c) => c.id !== id);
    // Ensure at least one primary
    if (remaining.length > 0 && !remaining.some((c) => c.is_primary)) {
      remaining[0].is_primary = true;
    }
    update("contacts", remaining);
  };

  const updateContact = (id: string, field: keyof ProviderContact, value: any) => {
    update(
      "contacts",
      form.contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const setPrimary = (id: string) => {
    update(
      "contacts",
      form.contacts.map((c) => ({ ...c, is_primary: c.id === id }))
    );
  };

  return (
    <div className="space-y-8">
      <section className="border border-border bg-card p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-subheadline">Ansprechpartner</h3>
            <p className="text-caption mt-1">
              Ein oder mehrere Kontaktpersonen für diesen Provider
            </p>
          </div>
          <Button type="button" variant="quiet" size="sm" onClick={addContact}>
            <Plus className="w-4 h-4 mr-1" />
            Kontakt hinzufügen
          </Button>
        </div>

        {form.contacts.length === 0 && (
          <div className="p-8 border border-dashed border-border text-center">
            <p className="text-caption mb-4">
              Noch keine Ansprechpartner angelegt
            </p>
            <Button type="button" variant="quiet" size="sm" onClick={addContact}>
              <Plus className="w-4 h-4 mr-1" />
              Ersten Kontakt anlegen
            </Button>
          </div>
        )}

        {form.contacts.map((contact, idx) => (
          <div
            key={contact.id}
            className="border border-border p-5 space-y-4 relative"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-micro">Kontakt {idx + 1}</span>
                {contact.is_primary && (
                  <span className="text-xs px-2 py-0.5 bg-accent/10 text-accent border border-accent/20">
                    Hauptkontakt
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {!contact.is_primary && (
                  <button
                    type="button"
                    onClick={() => setPrimary(contact.id)}
                    title="Als Hauptkontakt setzen"
                    className="p-1.5 text-muted-foreground hover:text-accent transition-colors"
                  >
                    <Star className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeContact(contact.id)}
                  title="Kontakt entfernen"
                  className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  value={contact.name}
                  onChange={(e) => updateContact(contact.id, "name", e.target.value)}
                  placeholder="Max Mustermann"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Rolle / Position</label>
                <input
                  value={contact.role}
                  onChange={(e) => updateContact(contact.id, "role", e.target.value)}
                  placeholder="z.B. Vertrieb, Technik"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>E-Mail *</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => updateContact(contact.id, "email", e.target.value)}
                  placeholder="kontakt@unternehmen.de"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Telefon</label>
                <input
                  value={contact.phone}
                  onChange={(e) => updateContact(contact.id, "phone", e.target.value)}
                  placeholder="+49 ..."
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        ))}
      </section>

      {form.contacts.length > 0 && (
        <section className="border border-border bg-card p-6 space-y-4">
          <h3 className="text-subheadline">Übersicht</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 border border-border bg-secondary/20">
              <span className="text-micro block mb-1">Anzahl</span>
              <p className="text-sm font-medium">{form.contacts.length} Kontakt(e)</p>
            </div>
            <div className="p-4 border border-border bg-secondary/20">
              <span className="text-micro block mb-1">Hauptkontakt</span>
              <p className="text-sm font-medium">
                {form.contacts.find((c) => c.is_primary)?.name || "—"}
              </p>
            </div>
            <div className="p-4 border border-border bg-secondary/20">
              <span className="text-micro block mb-1">Hauptkontakt E-Mail</span>
              <p className="text-sm font-medium truncate">
                {form.contacts.find((c) => c.is_primary)?.email || "—"}
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
